import { QuizQuestion } from '../components/QuizQuestions';

/**
 * Validate a quiz answer based on question type
 */
export function validateQuizAnswer(question: QuizQuestion, userAnswer: any): boolean {
  switch (question.questionType) {
    case 'MULTIPLE_CHOICE':
      return userAnswer === question.correctAnswer;
    
    case 'MULTIPLE_SELECT': {
      if (!Array.isArray(userAnswer) || !Array.isArray(question.correctAnswers)) {
        return false;
      }
      const sortedUser = [...userAnswer].sort((a, b) => a - b);
      const sortedCorrect = [...question.correctAnswers].sort((a, b) => a - b);
      return sortedUser.length === sortedCorrect.length &&
        sortedUser.every((val, idx) => val === sortedCorrect[idx]);
    }
    
    case 'TRUE_FALSE':
      return userAnswer === question.correctAnswer;
    
    case 'FILL_BLANK': {
      if (!userAnswer || typeof userAnswer !== 'object') {
        return false;
      }
      return question.blanks.every(blank => {
        const userInput = userAnswer[blank.id]?.trim() || '';
        if (blank.caseSensitive) {
          return blank.acceptedAnswers.includes(userInput);
        }
        return blank.acceptedAnswers.some(
          accepted => accepted.toLowerCase() === userInput.toLowerCase()
        );
      });
    }
    
    case 'TERM_MATCHING': {
      if (!userAnswer || typeof userAnswer !== 'object') {
        return false;
      }
      return question.pairs.every(pair => 
        userAnswer[pair.term] === pair.definition
      );
    }
    
    default:
      return false;
  }
}

/**
 * Calculate quiz results from all questions and answers
 */
export function calculateQuizResults(
  questions: QuizQuestion[],
  userAnswers: Record<string, any>
): {
  totalQuestions: number;
  correctAnswers: number;
  score: number;
  passed: boolean;
} {
  let correct = 0;
  
  questions.forEach((question, index) => {
    const userAnswer = userAnswers[index];
    if (validateQuizAnswer(question, userAnswer)) {
      correct++;
    }
  });
  
  const score = Math.round((correct / questions.length) * 100);
  
  return {
    totalQuestions: questions.length,
    correctAnswers: correct,
    score,
    passed: score >= 70 // 70% passing threshold
  };
}

/**
 * Check if user has answered a specific question
 */
export function hasAnsweredQuestion(question: QuizQuestion, userAnswer: any): boolean {
  if (userAnswer === undefined || userAnswer === null) {
    return false;
  }
  
  switch (question.questionType) {
    case 'MULTIPLE_CHOICE':
      return typeof userAnswer === 'number';
    
    case 'MULTIPLE_SELECT':
      return Array.isArray(userAnswer) && userAnswer.length > 0;
    
    case 'TRUE_FALSE':
      return typeof userAnswer === 'boolean';
    
    case 'FILL_BLANK':
      if (!userAnswer || typeof userAnswer !== 'object') {
        return false;
      }
      // Check if at least one blank is filled
      return question.blanks.some(blank => {
        const input = userAnswer[blank.id]?.trim();
        return input && input.length > 0;
      });
    
    case 'TERM_MATCHING':
      if (!userAnswer || typeof userAnswer !== 'object') {
        return false;
      }
      // Check if ALL matches are made (user must submit to enable Next)
      return question.pairs.every(pair => userAnswer[pair.term] && userAnswer[pair.term].trim().length > 0);
    
    default:
      return false;
  }
}

